import { useState } from 'react';
import {
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { FileText, TrendingUp, Clock, Timer, AlertTriangle } from 'lucide-react';
import { useActiveSlaTemplates } from '@/api/services';
import { useApplyServiceSlaTemplate } from '@/api/services-mutations';
import { SLA_TIER_TONE, formatMinutes } from './sla-constants';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const metric = { display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' } as const;

/**
 * Apply SLA template — pick one of the active Bronze/Silver/Gold/Platinum
 * tiers and attach it to the service (`POST /services/:id/sla/apply-template`).
 */
export function SlaTemplatePickerDialog({
  serviceId,
  open,
  onOpenChange,
}: {
  serviceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const templates = useActiveSlaTemplates(open);
  const apply = useApplyServiceSlaTemplate(serviceId);
  const [selected, setSelected] = useState<number | null>(null);

  if (!open) return null;

  const rows = templates.data ?? [];

  const submit = () => {
    if (!selected) return;
    apply.mutate(selected, {
      onSuccess: () => { toast.success('SLA template applied'); setSelected(null); onOpenChange(false); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Apply SLA template</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-3)' }}>
            Pick an SLA template to apply to this service.
          </p>
          {templates.isLoading ? (
            <p style={{ color: 'var(--vn-fg-muted)' }}>Loading templates…</p>
          ) : rows.length === 0 ? (
            <EmptyState icon={<FileText size={22} />} title="No SLA templates" description="There are no active SLA templates." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--vn-space-3)' }}>
              {rows.map((tpl) => {
                const isSel = selected === tpl.sla_template_id;
                return (
                  <button
                    key={tpl.sla_template_id}
                    type="button"
                    onClick={() => setSelected(tpl.sla_template_id)}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--vn-space-3)',
                      borderRadius: 'var(--vn-radius-md)',
                      border: `1px solid ${isSel ? 'var(--vn-primary-solid)' : 'var(--vn-border-default)'}`,
                      background: isSel ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                      cursor: 'pointer',
                      display: 'grid',
                      gap: 'var(--vn-space-2)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{tpl.template_name}</span>
                      <Badge tone={SLA_TIER_TONE[tpl.template_tier] ?? 'neutral'} appearance="subtle" size="sm">
                        {String(tpl.template_tier).toUpperCase()}
                      </Badge>
                    </div>
                    {tpl.template_description ? (
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{tpl.template_description}</span>
                    ) : null}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-1)', marginTop: 'var(--vn-space-1)' }}>
                      <span style={metric}><TrendingUp size={13} /> {tpl.uptime_guarantee}% uptime</span>
                      <span style={metric}><Clock size={13} /> {tpl.default_response_time}m response</span>
                      <span style={metric}><Timer size={13} /> {tpl.default_resolution_time}m resolve</span>
                      <span style={metric}><AlertTriangle size={13} /> {formatMinutes(tpl.downtime_allowance_minutes)}/month</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={apply.isPending}>Cancel</Button>
          <Button variant="primary" disabled={!selected || apply.isPending} onClick={submit}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
